(function () {
  'use strict';

  const rootSelector = 'cart-items.olitans-cart-page';
  let observer = null;
  let externalWidgetObserver = null;
  let externalWidgetTimer = null;
  let cartQuantityRequestId = 0;

  function getRoot() {
    return document.querySelector(rootSelector);
  }

  function getAjaxRoute(route, fallback) {
    if (typeof getShopifyAjaxRoute === 'function') {
      return getShopifyAjaxRoute(route, fallback);
    }

    const source = route || `/${fallback.replace(/^\/+/, '')}`;
    const hashIndex = source.indexOf('#');
    const hash = hashIndex > -1 ? source.slice(hashIndex) : '';
    const beforeHash = hashIndex > -1 ? source.slice(0, hashIndex) : source;
    const queryIndex = beforeHash.indexOf('?');
    const query = queryIndex > -1 ? beforeHash.slice(queryIndex) : '';
    let pathname = queryIndex > -1 ? beforeHash.slice(0, queryIndex) : beforeHash;

    if (!/\.js$/.test(pathname)) {
      pathname = pathname.replace(/\/$/, '') + '.js';
    }

    return `${pathname}${query}${hash}`;
  }

  function normalizeShippingMessage(root) {
    if (!root) return;

    const message = root.querySelector('.haloCalculatorShipping .message');
    const meter = root.querySelector('[data-free-shipping-progress-meter], .haloCalculatorShipping .progress-meter');

    if (!message || !meter) return;

    const text = (message.textContent || '').replace(/\s+/g, ' ').trim();
    const meterStyle = meter.style.width || meter.getAttribute('style') || '0';
    const widthMatch = String(meterStyle).match(/[\d.]+/);
    const progressWidth = widthMatch ? parseFloat(widthMatch[0]) : 0;
    const isUnlocked = progressWidth >= 99 || /qualif|eligible|unlock|free shipping/i.test(text);
    const wrapper = message.closest('.haloCalculatorShipping');

    if (wrapper) {
      wrapper.classList.toggle('olitans-cart-page-shipping-unlocked', isUnlocked);
    }

    if (isUnlocked) {
      message.textContent = '✓ Free Shipping Unlocked';
      message.classList.add('progress-free');
      return;
    }

    message.classList.remove('progress-free');

    const moneyMatch = text.match(/(?:US)?\$\s?[\d,.]+|[\d,.]+\s?(?:USD|EUR|GBP|CAD|AUD)/i);

    if (moneyMatch) {
      message.textContent = `Spend ${moneyMatch[0].replace(/\s+/g, ' ')} more to unlock free shipping.`;
    }
  }

  function syncCartCount(root, cart) {
    if (!root || !cart || typeof cart.item_count !== 'number') return;

    const subtitle = root.querySelector('.olitans-cart-page__subtitle');
    if (!subtitle) return;

    subtitle.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'Item' : 'Items'}`;
  }

  function syncCheckoutFooter(root) {
    if (!root) return;

    const subtotal = root.querySelector('[data-cart-total] .cart-total-subtotal .cart-total-value .text');
    const footerSubtotal = root.querySelector('.olitans-cart-checkout-footer__summary strong');

    if (subtotal && footerSubtotal) {
      footerSubtotal.textContent = subtotal.textContent.trim();
    }
  }

  function formatCartMoney(cents) {
    if (typeof cents !== 'number') return '';

    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.money_format);
    }

    return `$${(cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  function findCartItem(cart, lineKey) {
    if (!cart || !Array.isArray(cart.items)) return null;

    return cart.items.find((item) => item.key === lineKey) || null;
  }

  function findCartRow(root, lineKey) {
    if (!root || !lineKey) return null;

    return Array.from(root.querySelectorAll('[data-item-row][data-line]')).find((row) => {
      return row.dataset.line === lineKey;
    }) || null;
  }

  function getEstimatedSubtotal(root) {
    if (!root) return null;

    return Array.from(root.querySelectorAll('[data-item-row][data-line-price]')).reduce((total, row) => {
      const linePrice = parseInt(row.dataset.linePrice, 10);
      return Number.isNaN(linePrice) ? total : total + linePrice;
    }, 0);
  }

  function getEstimatedItemCount(root) {
    if (!root) return null;

    return Array.from(root.querySelectorAll('[data-item-row][data-line-quantity]')).reduce((total, row) => {
      const lineQuantity = parseInt(row.dataset.lineQuantity, 10);
      return Number.isNaN(lineQuantity) ? total : total + lineQuantity;
    }, 0);
  }

  function syncVisibleSubtotal(root, subtotalCents) {
    if (!root || typeof subtotalCents !== 'number') return;

    const subtotal = formatCartMoney(subtotalCents);

    root.querySelectorAll('[data-cart-total] .cart-total-subtotal .cart-total-value .text, .olitans-cart-checkout-footer__summary strong').forEach((element) => {
      element.innerHTML = subtotal;
    });
  }

  function syncVisibleCartCount(root, itemCount) {
    if (!root || typeof itemCount !== 'number') return;

    syncCartCount(root, { item_count: itemCount });

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = itemCount >= 100 && window.cartStrings?.item_99
        ? window.cartStrings.item_99
        : String(itemCount);
    });
  }

  function updateCartValuesOptimistically(root, lineKey, nextQuantity) {
    const lineRow = findCartRow(root, lineKey);
    if (!lineRow) return;

    const unitPrice = parseInt(lineRow.dataset.unitPrice, 10);
    if (Number.isNaN(unitPrice)) return;

    const nextLinePrice = unitPrice * nextQuantity;
    const priceTarget = lineRow.querySelector('[data-item-price-with-quantity-display], .cart-item-value');

    lineRow.dataset.linePrice = String(nextLinePrice);
    lineRow.dataset.lineQuantity = String(nextQuantity);

    if (priceTarget) {
      priceTarget.innerHTML = formatCartMoney(nextLinePrice);
    }

    const estimatedSubtotal = getEstimatedSubtotal(root);
    const estimatedItemCount = getEstimatedItemCount(root);

    if (typeof estimatedSubtotal === 'number') {
      syncVisibleSubtotal(root, estimatedSubtotal);
    }

    if (typeof estimatedItemCount === 'number') {
      syncVisibleCartCount(root, estimatedItemCount);
    }
  }

  function syncCartValuesFromAjax(root, cart, lineKey) {
    if (!root || !cart) return;

    const lineItem = findCartItem(cart, lineKey);
    const lineRow = findCartRow(root, lineKey);

    if (lineItem && lineRow) {
      const linePrice = typeof lineItem.final_line_price === 'number'
        ? lineItem.final_line_price
        : lineItem.line_price;
      const priceTarget = lineRow.querySelector('[data-item-price-with-quantity-display], .cart-item-value');

      if (priceTarget && typeof linePrice === 'number') {
        priceTarget.innerHTML = formatCartMoney(linePrice);
      }

      if (typeof linePrice === 'number') {
        lineRow.dataset.linePrice = String(linePrice);
      }

      if (typeof lineItem.quantity === 'number') {
        lineRow.dataset.lineQuantity = String(lineItem.quantity);

        if (lineItem.quantity > 0 && typeof linePrice === 'number') {
          lineRow.dataset.unitPrice = String(Math.round(linePrice / lineItem.quantity));
        }
      }
    }

    if (typeof cart.total_price === 'number') {
      syncVisibleSubtotal(root, cart.total_price);
    }

    if (typeof cart.item_count === 'number') {
      syncVisibleCartCount(root, cart.item_count);
    }
  }

  function hideElement(element) {
    if (!element || element.dataset.olitansCartPageWidgetHidden === 'true') return;

    element.dataset.olitansCartPageWidgetHidden = 'true';
    element.setAttribute('aria-hidden', 'true');
    element.style.setProperty('display', 'none', 'important');
    element.style.setProperty('visibility', 'hidden', 'important');
    element.style.setProperty('opacity', '0', 'important');
    element.style.setProperty('pointer-events', 'none', 'important');
  }

  function isThemeCartElement(element) {
    return Boolean(element.closest(`${rootSelector}, cart-coupon-discount, main, header, footer, .shopify-section`));
  }

  function hideExternalCartWidgets() {
    if (!document.body || !document.body.classList.contains('template-cart')) return;

    const knownSelectors = [
      '#shopify-chat',
      '#ShopifyChat',
      '[id*="shopify-chat" i]',
      '[class*="shopify-chat" i]',
      'iframe[title*="chat" i]',
      'iframe[src*="shopify-chat" i]',
      'iframe[src*="inbox" i]',
      'iframe[src*="gorgias" i]',
      'iframe[src*="intercom" i]',
      'iframe[src*="tidio" i]',
      '[class*="chat-launcher" i]',
      '[class*="chat-widget" i]',
      '[class*="chat-button" i]',
      '[id*="chat-launcher" i]',
      '[id*="chat-widget" i]',
      '[class*="privy" i]',
      '[id*="privy" i]',
      '[class*="justuno" i]',
      '[id*="justuno" i]',
      '[class*="wheelio" i]',
      '[id*="wheelio" i]',
      '[class*="coupon" i][style*="position: fixed"]',
      '[class*="discount" i][style*="position: fixed"]',
      '[class*="floating" i][style*="position: fixed"]',
      '[id*="coupon" i][style*="position: fixed"]',
      '[id*="discount" i][style*="position: fixed"]',
      '[id*="floating" i][style*="position: fixed"]'
    ];

    document.querySelectorAll(knownSelectors.join(',')).forEach((element) => {
      if (!isThemeCartElement(element)) {
        hideElement(element);
      }
    });

    const textMatcher = /get\s*10\s*%?\s*off/i;
    const candidates = [
      ...document.querySelectorAll('body > *'),
      ...document.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]')
    ];

    candidates.forEach((element) => {
      if (!(element instanceof HTMLElement) || isThemeCartElement(element)) return;
      if (element.matches('script, style, link, meta')) return;

      const text = [
        element.textContent,
        element.getAttribute('aria-label'),
        element.getAttribute('title')
      ].join(' ');

      if (!textMatcher.test(text)) return;

      const styles = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const isEdgeWidget = styles.position === 'fixed' && (
        rect.left < 96 ||
        rect.right > window.innerWidth - 96 ||
        rect.bottom > window.innerHeight - 160
      );

      if (isEdgeWidget) {
        hideElement(element);
      }
    });
  }

  function watchExternalCartWidgets() {
    hideExternalCartWidgets();

    [250, 800, 1600, 3000].forEach((delay) => {
      window.setTimeout(hideExternalCartWidgets, delay);
    });

    if (externalWidgetObserver || !document.body) return;

    externalWidgetObserver = new MutationObserver(() => {
      window.clearTimeout(externalWidgetTimer);
      externalWidgetTimer = window.setTimeout(hideExternalCartWidgets, 80);
    });

    externalWidgetObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function showCartWarning(message) {
    const fallback = typeof getJsonRequestErrorMessage === 'function'
      ? getJsonRequestErrorMessage(message, 'Cart request failed. Please refresh the page and try again.')
      : (message || 'Cart request failed. Please refresh the page and try again.');

    if (window.halo && typeof halo.showWarning === 'function') {
      halo.showWarning(fallback);
    } else if (typeof showWarning === 'function') {
      showWarning(fallback);
    } else {
      console.error(fallback);
    }
  }

  function getLineInventoryPolicy(wrapper, input) {
    if (!wrapper || !input) return null;

    const policyArray = window[`cart_selling_array_${wrapper.dataset.product}`];
    return policyArray ? policyArray[wrapper.dataset.variant || input.dataset.cartQuantityId] : null;
  }

  function setQuantityBusy(wrapper, isBusy) {
    if (!wrapper) return;

    wrapper.classList.toggle('is-updating', isBusy);
    wrapper.dataset.olitansUpdating = isBusy ? 'true' : 'false';
  }

  function findQuantityWrapperByLine(root, lineKey) {
    if (!root || !lineKey) return null;

    return Array.from(root.querySelectorAll('cart-update-quantity')).find((wrapper) => {
      const input = wrapper.querySelector('[data-cart-quantity]');
      return input?.dataset.line === lineKey;
    }) || null;
  }

  function clearQuantityBusy(root) {
    if (!root) return;

    root.querySelectorAll('cart-update-quantity.is-updating').forEach((wrapper) => {
      setQuantityBusy(wrapper, false);
    });
  }

  function releaseQuantityBusy(wrapper, lineKey, delay = 0) {
    window.setTimeout(() => {
      const root = getRoot();
      const currentWrapper = findQuantityWrapperByLine(root, lineKey) || wrapper;

      setQuantityBusy(currentWrapper, false);
      syncQuantityButtons(root);
      syncCheckoutFooter(root);
    }, delay);
  }

  function changeCartLine(lineKey, index, quantity) {
    if (typeof fetch === 'function') {
      const body = JSON.stringify({
        id: lineKey,
        quantity,
        sections_url: window.location.pathname
      });

      return fetch(getAjaxRoute(window.routes && window.routes.cart_change_url, 'cart/change.js'), {
        ...(typeof fetchConfig === 'function' ? fetchConfig() : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        }),
        body
      }).then(async (response) => {
        const parsed = await response.json();

        if (!response.ok || parsed.errors || parsed.status >= 400) {
          throw new Error(parsed.description || parsed.message || parsed.errors || 'Cart request failed.');
        }

        return parsed;
      });
    }

    if (window.Shopify && typeof Shopify.changeItem === 'function') {
      return new Promise((resolve) => {
        Shopify.changeItem(lineKey, quantity, index, resolve);
      });
    }

    return Promise.reject(new Error('Cart request failed. Please refresh the page and try again.'));
  }

  function syncQuantityButtons(root) {
    if (!root) return;

    root.querySelectorAll('cart-update-quantity').forEach((wrapper) => {
      const input = wrapper.querySelector('[data-cart-quantity]');
      const minus = wrapper.querySelector('.btn-quantity.minus');
      const value = parseInt(input?.value, 10) || 1;
      const isMinimum = value <= 1;

      if (!minus || wrapper.classList.contains('is-updating')) return;

      minus.classList.toggle('is-disabled', isMinimum);
      minus.setAttribute('aria-disabled', isMinimum ? 'true' : 'false');
    });
  }

  async function updateCartQuantity(button) {
    const wrapper = button.closest('cart-update-quantity');
    const input = wrapper?.querySelector('[data-cart-quantity]');
    const lineKey = input?.dataset.line;
    const index = input?.dataset.index;

    if (!wrapper || !input || !lineKey || !index || wrapper.classList.contains('is-updating')) return;

    const currentQuantity = parseInt(input.value, 10) || 1;
    const isIncrease = button.matches('.plus');
    const isDecrease = button.matches('.minus');
    let nextQuantity = currentQuantity;

    if (isIncrease) {
      nextQuantity = currentQuantity + 1;
    } else if (isDecrease) {
      nextQuantity = currentQuantity <= 1 ? 1 : currentQuantity - 1;
    }

    if (nextQuantity === currentQuantity) return;

    const stock = parseInt(input.dataset.inventoryQuantity, 10);
    const policy = getLineInventoryPolicy(wrapper, input);

    if (isIncrease && !Number.isNaN(stock) && stock > 0 && nextQuantity > stock && policy === 'deny') {
      nextQuantity = stock;
      showCartWarning(typeof getInputMessage === 'function' ? getInputMessage(stock) : `Only ${stock} available.`);
    }

    const requestId = ++cartQuantityRequestId;
    const previousQuantity = currentQuantity;

    input.value = nextQuantity;
    input.dataset.cartLastSubmittedQuantity = String(nextQuantity);
    updateCartValuesOptimistically(getRoot(), lineKey, nextQuantity);
    setQuantityBusy(wrapper, true);
    let shouldReleaseBusy = true;

    try {
      const cart = await changeCartLine(lineKey, index, nextQuantity);

      if (!cart || cart.errors || cart.status >= 400) {
        throw new Error(cart?.description || cart?.message || cart?.errors || 'Cart request failed.');
      }

      if (requestId !== cartQuantityRequestId) return;

      syncCartValuesFromAjax(getRoot(), cart, lineKey);

      if (window.halo && typeof halo.updateCart === 'function') {
        halo.updateCart(cart);
      } else {
        document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
      }
    } catch (error) {
      shouldReleaseBusy = false;
      input.value = previousQuantity;
      input.dataset.cartLastSubmittedQuantity = String(previousQuantity);
      updateCartValuesOptimistically(getRoot(), lineKey, previousQuantity);
      setQuantityBusy(wrapper, false);
      syncQuantityButtons(getRoot());
      showCartWarning(error);
    } finally {
      if (shouldReleaseBusy) {
        releaseQuantityBusy(wrapper, lineKey, 120);
      }
    }
  }

  function bindQuantityControls(root) {
    if (!root || root.dataset.olitansCartQuantityBound === 'true') return;

    root.dataset.olitansCartQuantityBound = 'true';
    root.addEventListener('click', (event) => {
      const button = event.target.closest('.cart-item-qty .btn-quantity');
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (button.getAttribute('aria-disabled') === 'true') return;
      updateCartQuantity(button);
    }, true);

    syncQuantityButtons(root);
  }

  function refreshCartFromExistingFlow(button) {
    if (!window.Shopify || typeof Shopify.getCart !== 'function') {
      document.dispatchEvent(new CustomEvent('cart-update'));
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      Shopify.getCart((cart) => {
        if (window.halo && typeof halo.updateCart === 'function') {
          halo.updateCart(cart);
        } else {
          document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
        }

        resolve(cart);
      }, reject);
    }).finally(() => {
      if (button) {
        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = button.dataset.defaultText || 'Add';
          button.classList.remove('is-added');
        }, 900);
      }
    });
  }

  async function addCarryItem(button) {
    const variantId = Number(button.dataset.variantId);

    if (!variantId || button.disabled) return;

    button.dataset.defaultText = button.dataset.defaultText || button.textContent.trim() || 'Add';
    button.disabled = true;
    button.textContent = 'Adding';

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(getAjaxRoute(window.routes && window.routes.cart_add_url, 'cart/add.js'), {
        ...(typeof fetchConfig === 'function' ? fetchConfig() : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        }),
        body
      });

      const parsed = await response.json();

      if (!response.ok || parsed.errors || parsed.status >= 400) {
        throw new Error(parsed.description || parsed.message || 'Cart request failed.');
      }

      button.textContent = 'Added';
      button.classList.add('is-added');
      await refreshCartFromExistingFlow(button);
    } catch (error) {
      button.disabled = false;
      button.textContent = button.dataset.defaultText || 'Add';

      if (window.halo && typeof halo.showWarning === 'function') {
        halo.showWarning(typeof getJsonRequestErrorMessage === 'function'
          ? getJsonRequestErrorMessage(error, 'Cart request failed. Please refresh the page and try again.')
          : (error.message || 'Cart request failed. Please refresh the page and try again.'));
      } else if (typeof showWarning === 'function') {
        showWarning(typeof getJsonRequestErrorMessage === 'function'
          ? getJsonRequestErrorMessage(error, 'Cart request failed. Please refresh the page and try again.')
          : (error.message || 'Cart request failed. Please refresh the page and try again.'));
      } else {
        console.error(error);
      }
    }
  }

  function bindRecommendationAdds(root) {
    if (!root || root.dataset.olitansCartPageBound === 'true') return;

    root.dataset.olitansCartPageBound = 'true';
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-olitans-cart-page-add]');
      if (!button) return;

      event.preventDefault();
      addCarryItem(button);
    });
  }

  function watchCartChanges(root) {
    if (!root || observer) return;

    observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => {
        normalizeShippingMessage(root);
        syncQuantityButtons(root);
        syncCheckoutFooter(root);
      });
    });

    const target = root.querySelector('#main-cart-items') || root;
    observer.observe(target, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    watchExternalCartWidgets();

    const root = getRoot();
    if (!root) return;

    bindRecommendationAdds(root);
    bindQuantityControls(root);
    clearQuantityBusy(root);
    normalizeShippingMessage(root);
    syncQuantityButtons(root);
    syncCheckoutFooter(root);
    watchCartChanges(root);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('cart-update', (event) => {
    window.requestAnimationFrame(() => {
      const root = getRoot();
      clearQuantityBusy(root);
      normalizeShippingMessage(root);
      syncCartCount(root, event.detail);
      syncQuantityButtons(root);
      syncCheckoutFooter(root);
    });
  });

  if (document.readyState !== 'loading') {
    init();
  }
})();
